import clsx from "clsx";
import Modal from "../Modal/Modal";
import { ChangeEvent, useRef, useState } from "react";

import {
  MdCheck,
  MdClose,
  MdDelete,
  MdDownload,
  MdEdit,
  MdRedo,
  MdUndo,
} from "react-icons/md";
import { useTranslation } from "react-i18next";
import { ReactSketchCanvas, ReactSketchCanvasRef } from "react-sketch-canvas";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { FaEraser } from "react-icons/fa6";
import ColorPicker from "../ColorPicker/ColorPicker";
import { rgbaObjectToString, showNotification } from "../../helpers/utils";
import { saveAs } from "file-saver";
import tasksStore from "../../store/tasksStore";
import useNotes from "../../hooks/useNotes";
import useMobile from "../../hooks/useMobile";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface ICanvasModal {
  isLoading?: boolean;
  onClose: () => void;
}

const CanvasModal: React.FC<ICanvasModal> = ({
  isLoading = false,
  onClose = () => {},
}) => {
  const { isMobile } = useMobile();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const { resetCache } = useNotes();
  const { t } = useTranslation(["common"]);
  const canvasRef = useRef<ReactSketchCanvasRef>(null);
  const [eraseMode, setEraseMode] = useState(false);
  const { addTask } = tasksStore();
  const [isSavingNote, setIsSavingNote] = useState<boolean>(false);

  const handleEraserClick = () => {
    setEraseMode(true);
    canvasRef.current?.eraseMode(true);
  };

  const handlePenClick = () => {
    setEraseMode(false);
    canvasRef.current?.eraseMode(false);
  };

  const handleUndoClick = () => {
    canvasRef.current?.undo();
  };

  const handleRedoClick = () => {
    canvasRef.current?.redo();
  };

  const handleClearClick = () => {
    canvasRef.current?.clearCanvas();
  };

  const handleResetClick = () => {
    canvasRef.current?.resetCanvas();
  };

  const [strokeColor, setStrokeColor] = useState<any>({
    r: 0,
    g: 0,
    b: 0,
    a: 1,
  });
  const [canvasColor, setCanvasColor] = useState<any>({
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  });
  const [strokeWidth, setStrokeWidth] = useState(5);
  const [eraserWidth, setEraserWidth] = useState(10);

  const handleStrokeWidthChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStrokeWidth(+event.target.value);
  };

  const handleEraserWidthChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEraserWidth(+event.target.value);
  };

  const saveNote = async () => {
    const paths = await canvasRef?.current?.exportPaths();
    setIsSavingNote(true);

    await addTask({
      onError: (e) => {
        showNotification({
          message: t("notes.error_adding_note"),
          kind: "error",
        });
        setIsSavingNote(false);
      },
      content: JSON.stringify(paths),
      onResolved: async (e) => {
        await resetCache();
        showNotification({
          message: t("notes.note_added_message"),
          kind: "success",
        });
        setIsSavingNote(false);
        onClose();
      },
      type: "CANVAS",
    });
  };

  return (
    <Modal
      width={clsx([
        {
          "min-w-[90%] max-w-[90%] w-full": !isLoading,
        },
      ])}
      className={clsx(["!min-w-0 md:!w-[calc(100vw-40px)] md:!max-w-[1400px]"])}
      onEscape={() => {
        if (!isSavingNote) {
          onClose?.();
        }
      }}
      closeOnOverlayClick={false}
    >
      <Modal.Header
        title="Dibuja"
        onClose={() => {
          if (!isSavingNote) {
            onClose?.();
          }
        }}
      />
      <Modal.Body
        className="h-full md:!max-h-[97vh] !max-h-[80vh]"
        focus={false}
      >
        <div
          className={clsx([
            "flex",
            "flex-col",
            "gap-2",
            "h-full",
            "md:!max-h-[97vh]",
            "!max-h-[80vh]",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "flex-col",
              "md:flex-row",
              "gap-2",
              "justify-between",
              "items-center",
              "-top-[1px]",
              "sticky",
              "bg-white dark:bg-gray-800 dark:text-white",
              "py-2",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "md:flex-row",
                "items-center",
                "gap-1",
                "w-full",
              ])}
            >
              <ColorPicker
                color={canvasColor}
                onChange={(color) => {
                  setCanvasColor(color.rgb);
                }}
                textColor={strokeColor}
                showText={false}
                className={clsx([
                  "md:!h-[46px]",
                  "!h-[40px]",
                  "md:!w-[46px]",
                  "!w-[40px]",
                  "md:shadow shadow",
                  // "!rounded-full",
                  "overflow-hidden",
                ])}
              />
              <ColorPicker
                color={strokeColor}
                onChange={(color) => {
                  setStrokeColor(color.rgb);
                }}
                textColor={canvasColor}
                showText={false}
                className={clsx([
                  "md:!h-[46px]",
                  "!h-[40px]",
                  "md:!w-[46px]",
                  "!w-[40px]",
                  "md:shadow shadow",
                  // "!rounded-full",
                  "overflow-hidden",
                ])}
              />

              {!eraseMode && (
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={strokeWidth}
                  onChange={handleStrokeWidthChange}
                  className="w-full md:w-1/2"
                />
              )}

              {eraseMode && (
                <input
                  type="range"
                  min="1"
                  max="100"
                  step="1"
                  value={eraserWidth}
                  onChange={handleEraserWidthChange}
                  className="w-full md:w-1/2"
                />
              )}
            </div>
            <div className={clsx(["flex", "w-full"])}>
              <ButtonGroup
                gap={1}
                align={"right"}
                buttons={[
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: handlePenClick,
                    icon: eraseMode ? (
                      <MdEdit size={ICON_SIZES.SM} />
                    ) : (
                      <MdEdit
                        size={ICON_SIZES.SM}
                        className={clsx(["text-red-500", "dark:text-lime-400"])}
                      />
                    ),
                    disabled: !eraseMode,
                  },
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: handleEraserClick,
                    icon: !eraseMode ? (
                      <FaEraser size={ICON_SIZES.SM} />
                    ) : (
                      <FaEraser
                        size={ICON_SIZES.SM}
                        className={clsx(["text-red-500", "dark:text-lime-400"])}
                      />
                    ),

                    disabled: eraseMode,
                  },
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: handleUndoClick,
                    icon: <MdUndo size={ICON_SIZES.SM} />,
                  },
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: handleRedoClick,
                    icon: <MdRedo size={ICON_SIZES.SM} />,
                  },
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: handleClearClick,
                    icon: <MdDelete size={ICON_SIZES.SM} />,
                  },
                  {
                    kind: EButtonKind.NORMAL,
                    onClick: async () => {
                      setIsDownloading(true);
                      await canvasRef?.current
                        ?.exportImage("png")
                        .then((data) => {
                          saveAs(data, "eXpenser.net Note.png");
                          showNotification({
                            kind: "success",
                            message: "Imagen descargada correctamente",
                          });
                        })
                        .catch((e) => {
                          showNotification({
                            kind: "error",
                            message: "Error descargando tu imagen",
                          });
                        })
                        .finally(() => setIsDownloading(false));
                    },
                    icon: <MdDownload size={ICON_SIZES.SM} />,
                    isLoading: isDownloading,
                    disabled: isDownloading,
                  },
                ]}
              />
            </div>
          </div>
          <div className={clsx(["flex", "overflow-auto"])}>
            <ReactSketchCanvas
              exportWithBackgroundImage
              ref={canvasRef}
              height={isMobile ? "45vh" : "65vh"}
              strokeColor={rgbaObjectToString(strokeColor)}
              canvasColor={rgbaObjectToString(canvasColor)}
              strokeWidth={strokeWidth}
              eraserWidth={eraserWidth}
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            icon: <MdCheck size={ICON_SIZES.SM} />,
            onClick: async () => {
              await saveNote();
            },
            kind: EButtonKind.SAVE,
            text: t("common:buttons.save"),
            disabled: isSavingNote,
            isLoading: isSavingNote,
          },
          {
            text: t("common:buttons.close"),
            onClick: () => {
              if (!isSavingNote) {
                onClose?.();
              }
            },
            disabled: isSavingNote,
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
          },
        ]}
        // leftContent={
        // <ButtonGroup
        //   align="left"
        //   buttons={[
        //     {
        //       icon: <MdKeyboard size={ICON_SIZES.SM} />,
        //       onClick: () => {
        //         onClose?.();
        //         setShowCreateNewTask(true);
        //       },
        //     },
        //   ]}
        //   gap={1}
        // />
        // <ButtonGroup
        //   buttons={
        //     [
        // {
        //   text: "Descargar",
        //   kind: "save",
        //   icon: <MdDownload size={ICON_SIZES.SM} />,
        //   onClick: () => {
        //     canvasRef?.current
        //       ?.exportImage("png")
        //       .then((data) => {
        //         saveAs(data, "eXpenser.net Note");
        //         showNotification({
        //           kind: "success",
        //           message: "Imagen descargada correctamente",
        //         });
        //       })
        //       .catch((e) => {
        //         showNotification({
        //           kind: "error",
        //           message: "Error descargando tu imagen",
        //         });
        //       });
        //   },
        // },
        // {
        //   icon: <MdCheck size={ICON_SIZES.SM} />,
        //   onClick: async () => {
        //     await saveNote();
        //   },
        //   kind: "save",
        //   text: "Guardar",
        // },
        //     ]
        //   }
        //   align="left"
        // />
        // }
      />
    </Modal>
  );
};

export default CanvasModal;
